/**
 * This run against a previous one, and what it failed to collect.
 *
 * Two things an operator cannot get from the alert feed. The feed reports what
 * happened; this reports what is *different* from last time, which is the only
 * way "three unknown emitters near the route" becomes a finding rather than a
 * description of any road on any afternoon.
 *
 * The gaps half is the part that changes what somebody does next. A run that
 * found nothing has two explanations — nothing was there, or we could not see —
 * and they demand opposite responses. Everything listed under Gaps is a case
 * where the second explanation is live.
 *
 * Every number here is computed on the server by subtraction, not by a model.
 * Sentinel narrates this; it does not produce it.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Box, Menu, MenuItem, Typography } from '@mui/material';
import {
  CollectionGap,
  ComparedEmitter,
  MissionRun,
  MissionRunComparison,
  compareMissionRuns,
} from '../../services/missionApi';
import { formatDelta, formatSpeed } from './format';

const GOLD = '#f5b942';
const RED = '#ef5350';
const GREEN = '#66bb6a';
const BLUE = '#42a5f5';

const GAP_TONE: Record<CollectionGap['severity'], string> = {
  critical: RED,
  warning: GOLD,
  info: '#8f9bb3',
};

const GAP_TITLE: Record<CollectionGap['kind'], string> = {
  no_rf_observer: 'No RF collection',
  signal_loss: 'Position lost',
  rf_coverage: 'Unheard stretches',
  stops_not_reached: 'Stops missed',
  no_route_intel: 'Route never scanned',
  stale_route_intel: 'Intel is for another route',
};

const Line: React.FC<{ label: string; value: string; tone?: string }> = ({ label, value, tone }) => (
  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, py: 0.1 }}>
    <Typography variant="caption" sx={{ color: 'text.secondary', flex: 1, minWidth: 0 }} noWrap>
      {label}
    </Typography>
    <Typography variant="caption" sx={{ fontWeight: 700, color: tone || 'text.primary', flexShrink: 0 }}>
      {value}
    </Typography>
  </Box>
);

const EmitterRow: React.FC<{ emitter: ComparedEmitter }> = ({ emitter }) => (
  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, py: 0.1 }}>
    <Typography variant="caption" noWrap sx={{ flex: 1, minWidth: 0, fontSize: '0.65rem' }}>
      {emitter.name || emitter.identifier}
    </Typography>
    <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.disabled', flexShrink: 0 }}>
      {emitter.signal_type} · {emitter.segment_count} seg
    </Typography>
  </Box>
);

export interface MissionCompareProps {
  missionId: number;
  /** Bumped by the host to force a refetch, e.g. after a monitor tick. */
  refreshKey?: number;
  units?: 'km' | 'mi';
  onError?: (message: string) => void;
}

const MissionCompare: React.FC<MissionCompareProps> = ({
  missionId, refreshKey = 0, units = 'mi', onError,
}) => {
  const [data, setData] = useState<MissionRunComparison | null>(null);
  const [referenceId, setReferenceId] = useState<number | undefined>();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [loading, setLoading] = useState(false);

  const errorRef = React.useRef(onError);
  errorRef.current = onError;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await compareMissionRuns(missionId, { reference_id: referenceId }));
    } catch (e: any) {
      // A mission that has never been run answers 400 here, which is a fact
      // about the mission rather than a failure worth shouting about.
      if (e?.response?.status !== 400) {
        errorRef.current?.(e?.response?.data?.detail || e?.message || 'Could not compare runs');
      }
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [missionId, referenceId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  if (!data) {
    return loading ? null : (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.65rem' }}>
        Nothing to compare yet. Drive a baseline run and the next run can be measured against it.
      </Typography>
    );
  }

  const { pace, emitters, threats, gaps, reference } = data;

  return (
    <Box sx={{ mt: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.35 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, flex: 1, minWidth: 0 }} noWrap>
          {reference ? `vs ${reference.label}` : 'This run'}
        </Typography>
        {data.available.length > 0 && (
          <Typography
            variant="caption"
            role="button"
            tabIndex={0}
            onClick={(e) => setAnchor(e.currentTarget)}
            sx={{ fontWeight: 700, cursor: 'pointer', color: 'text.secondary' }}
          >
            Change
          </Typography>
        )}
      </Box>

      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
        {data.available.map((r: MissionRun) => (
          <MenuItem
            key={r.id}
            selected={r.id === reference?.run.id}
            onClick={() => { setReferenceId(r.id); setAnchor(null); }}
            sx={{ fontSize: '0.75rem' }}
          >
            {r.kind === 'rehearsal' ? 'Baseline run' : 'Live run'} {r.run_number}
            {r.observed_speed_mps ? ` · ${formatSpeed(r.observed_speed_mps, units)}` : ''}
          </MenuItem>
        ))}
      </Menu>

      {data.reason && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.65rem', mb: 0.5 }}>
          {data.reason}
        </Typography>
      )}

      {pace && (
        <Box sx={{ mb: 0.5 }}>
          <Line
            label={pace.worst_stop
              ? `Worst at ${pace.worst_stop.label || `stop ${(pace.worst_stop.seq ?? 0) + 1}`}`
              : 'Pace'}
            value={pace.worst_stop ? formatDelta(pace.worst_stop.delta_s) : 'level with reference'}
            tone={pace.worst_stop
              ? ((pace.worst_stop.delta_s || 0) > 0 ? RED : GREEN)
              : GREEN}
          />
          {pace.not_yet_reached > 0 && (
            <Line label="Stops still ahead" value={String(pace.not_yet_reached)} />
          )}
        </Box>
      )}

      {emitters && (
        <Box sx={{ mb: 0.5 }}>
          <Line
            label="Emitters not seen on the reference"
            value={String(emitters.new_count)}
            tone={emitters.new_count > 0 ? GOLD : GREEN}
          />
          {emitters.new.slice(0, 5).map((e) => (
            <EmitterRow key={e.normalized_identifier} emitter={e} />
          ))}
          {emitters.absent_count > 0 && (
            // Worth surfacing rather than hiding: a corridor regular that has
            // gone quiet can mean the road changed, and can equally mean our
            // own scanner stopped working.
            <Line
              label="Reference emitters absent this run"
              value={String(emitters.absent_count)}
            />
          )}
        </Box>
      )}

      {threats && threats.new_count > 0 && (
        <Line
          label="Threats the reference never raised"
          value={String(threats.new_count)}
          tone={RED}
        />
      )}

      {gaps.length > 0 && (
        <Box sx={{ mt: 0.6 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.15 }}>
            Collection gaps
          </Typography>
          {gaps.map((gap) => (
            <Box key={gap.kind} sx={{ display: 'flex', gap: 0.6, py: 0.15 }}>
              <Box
                sx={{
                  width: 3,
                  flexShrink: 0,
                  borderRadius: 1,
                  bgcolor: GAP_TONE[gap.severity],
                }}
              />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.65rem', display: 'block' }}>
                  {GAP_TITLE[gap.kind] || gap.kind}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ fontSize: '0.63rem', color: 'text.secondary', display: 'block' }}
                >
                  {gap.detail}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {gaps.length === 0 && (
        <Typography variant="caption" sx={{ display: 'block', fontSize: '0.63rem', color: BLUE }}>
          Nothing was missed: RF collection, positions and stops are all accounted for.
        </Typography>
      )}
    </Box>
  );
};

export default MissionCompare;
